import React, { useState, useRef, useEffect } from 'react';
import { History, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';

const relTime = (ts) => {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
};

/**
 * "Recent Edits" peek — lists the last few builder changes (newest first)
 * with one-click revert. Sits next to the bookmark menu in the header.
 * `undoStack` entries are { label, ts } from useReportUndoRedo; clicking an
 * item calls revertTo(index) to roll back to the state before that change.
 */
export function RecentEditsMenu({ undoStack = [], revertTo }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Newest first, capped to the last 5 changes.
  const recent = undoStack
    .map((e, i) => ({ label: typeof e === 'string' ? 'Edit' : (e.label || 'Edit'), ts: typeof e === 'string' ? Date.now() : e.ts, index: i }))
    .slice(-5)
    .reverse();

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="ghost" size="icon"
        onClick={() => setOpen(o => !o)}
        disabled={undoStack.length === 0}
        className="w-6 h-6 text-muted-foreground"
        title="Recent edits"
        data-testid="recent-edits-btn"
      >
        <History className="w-3 h-3" />
      </Button>
      {open && (
        <div
          className="absolute right-0 top-7 z-50 w-72 rounded-xl border border-border/60 bg-popover shadow-xl p-1.5"
          data-testid="recent-edits-menu"
        >
          <p className="px-2 py-1 text-[10px] font-heading font-semibold text-muted-foreground uppercase tracking-wider">Recent Edits</p>
          {recent.length === 0 ? (
            <p className="px-2 py-2 text-[11px] text-muted-foreground/70" data-testid="recent-edits-empty">No changes yet</p>
          ) : (
            recent.map((item, k) => (
              <button
                key={item.index}
                onClick={() => { revertTo(item.index); setOpen(false); }}
                className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg text-left hover:bg-primary/10 group transition-colors"
                data-testid={`recent-edit-item-${k}`}
                title={`Revert to before: ${item.label}`}
              >
                <span className="min-w-0">
                  <span className="block text-[11px] text-foreground truncate">{item.label}</span>
                  <span className="block text-[9px] text-muted-foreground/80">{relTime(item.ts)}</span>
                </span>
                <RotateCcw className="w-3 h-3 text-muted-foreground/70 group-hover:text-primary flex-shrink-0" />
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
